/**
 * Composite Recurring on Overview V2.
 *
 * Source of truth is persisted Composite Temporal Pattern facts (tpi1_ / ci1_
 * membership), not the Overview buyer-live overlay used for Now.
 */
import type {
  OverviewDecisionSummary,
  OverviewPeriodConditionRow,
} from '../continuousSafety/overviewSiteConditionSummary';
import { presentCompositeAlertDetail } from '../incidents/compositeAlertPresentation';
import type { BuyerLiveItem } from '../incidents/liveAlertsReconciliation';
import {
  COMPOSITE_SUBJECT_KIND,
} from './overviewV2CompositeNow';

const INTERNAL_LOCATION = new Set([
  'worksafety', 'n/a', 'unknown', 'none', 'null', '-', 'shadow_compare', 'experimental_shadow',
]);

function text(value: unknown): string {
  return String(value || '').trim();
}

function buyerLocation(item: BuyerLiveItem): string {
  const raw = text(item.operational.composite_location_display);
  if (!raw || INTERNAL_LOCATION.has(raw.toLowerCase())) return 'configured area';
  return raw;
}

export function isCompositeRecurringItem(item: BuyerLiveItem): boolean {
  const op = item?.operational;
  if (!op || op.excluded === true) return false;
  if (text(op.trigger_kind) !== 'TEMPORAL_PATTERN') return false;
  if (text(op.event_kind) === 'PATTERN_ALERT') return false;
  return text(op.subject_kind) === COMPOSITE_SUBJECT_KIND
    || text(op.subject_type) === COMPOSITE_SUBJECT_KIND;
}

export function compositeRecurringSubjectFromItem(item: BuyerLiveItem) {
  if (!isCompositeRecurringItem(item)) return null;
  const ruleId = text(item.operational.composite_rule_id);
  if (!ruleId) return null;
  const scopeType = text(item.operational.composite_evaluated_scope_type) || 'camera';
  const scopeId = text(item.operational.composite_evaluated_scope_id)
    || text(item.operational.camera_id)
    || text(item.operational.site_id)
    || 'site';
  return {
    subjectKind: COMPOSITE_SUBJECT_KIND,
    compositeRuleId: ruleId,
    scopeType,
    scopeId,
    conditionId: ['composite', ruleId, scopeType, scopeId].join(':'),
  };
}

export function overviewPeriodRowFromCompositeRecurring(
  item: BuyerLiveItem,
): OverviewPeriodConditionRow | null {
  const subject = compositeRecurringSubjectFromItem(item);
  if (!subject) return null;
  const name = text(item.operational.composite_rule_name)
    || presentCompositeAlertDetail(item)?.ruleName
    || 'Composite';
  const location = buyerLocation(item);
  const membership = item.operational.pattern_membership || {};
  const count = Number(membership.source_occurrence_count ?? membership.observed_count);
  const recorded = Number.isFinite(count) ? count : null;
  const triggeredAt = text(membership.triggered_at)
    || text(item.operational.recorded_at)
    || text(item.operational.updated_at)
    || null;
  const patternIncidentId = text(item.operational.incident_id);
  return {
    conditionId: subject.conditionId,
    label: name,
    location,
    measuredPeriods: recorded || 0,
    comparisonStatus: 'Pattern recurring',
    tone: 'limited',
    zoneId: null,
    cameraId: text(item.operational.camera_id) || subject.scopeId,
    deviceId: text(item.operational.device_id) || null,
    conditionFamily: 'COMPOSITE_ALERT',
    incidentType: 'composite_pattern',
    recordedOccurrenceTotal: recorded,
    recordedOccurrenceLabel: recorded != null ? `${recorded} occurrences` : 'Keeps occurring',
    supportingActivityLabel: 'Keeps occurring',
    includedThroughAt: triggeredAt,
    subjectKind: COMPOSITE_SUBJECT_KIND,
    compositePatternIncidentId: patternIncidentId,
    compositeReviewPath: patternIncidentId
      ? `/live?incident_id=${encodeURIComponent(patternIncidentId)}`
      : null,
    compositeControlPath: `/acceptance/lifecycle?site_id=${encodeURIComponent(text(item.operational.site_id))}&subject_kind=COMPOSITE&composite_rule_id=${encodeURIComponent(subject.compositeRuleId)}&composite_scope_type=${encodeURIComponent(subject.scopeType)}&composite_scope_id=${encodeURIComponent(subject.scopeId)}&focus=control`,
    usableHours: null,
  };
}

function newerRecurring(left: BuyerLiveItem, right: BuyerLiveItem): BuyerLiveItem {
  const leftCount = Number(left.operational.pattern_membership?.observed_count || 0);
  const rightCount = Number(right.operational.pattern_membership?.observed_count || 0);
  if (rightCount !== leftCount) return rightCount > leftCount ? right : left;
  const leftTs = text(left.operational.recorded_at);
  const rightTs = text(right.operational.recorded_at);
  return rightTs > leftTs ? right : left;
}

export function selectCompositeRecurringSubjects(items: BuyerLiveItem[]): BuyerLiveItem[] {
  const latest = new Map<string, BuyerLiveItem>();
  for (const item of items) {
    const subject = compositeRecurringSubjectFromItem(item);
    if (!subject) continue;
    const previous = latest.get(subject.conditionId);
    latest.set(subject.conditionId, previous ? newerRecurring(previous, item) : item);
  }
  return Array.from(latest.values());
}

export function mergeOverviewV2CompositeRecurring(
  summary: OverviewDecisionSummary,
  items: BuyerLiveItem[] | null | undefined,
): OverviewDecisionSummary {
  const selected = selectCompositeRecurringSubjects(items || []);
  const rows = selected
    .map((item) => overviewPeriodRowFromCompositeRecurring(item))
    .filter((row): row is OverviewPeriodConditionRow => Boolean(row));
  if (!rows.length) return summary;
  const existingIds = new Set((summary.overTime.conditionRows || []).map((row) => row.conditionId));
  const additions = rows.filter((row) => !existingIds.has(row.conditionId));
  const replacements = (summary.overTime.conditionRows || []).map((row) => {
    const incoming = rows.find((item) => item.conditionId === row.conditionId);
    return incoming || row;
  });
  return {
    ...summary,
    overTime: {
      ...summary.overTime,
      conditionRows: [...replacements, ...additions.filter((row) => !existingIds.has(row.conditionId))],
    },
  };
}
